import MojitoCore from 'mojito/core';
import MojitoPresentation from 'mojito/presentation';
import MojitoServices from 'mojito/services';
import MojitoModules from 'mojito/modules';

import {toSlug} from '#core/utils/slugify.js';

import {Localization} from '#core/localization.js';

const {getState} = MojitoCore.Services.redux.store;
const NavigationPathBuilder = MojitoPresentation.Base.Navigation.NavigationPathBuilder;
const {NAVIGATION, PARAMETER} = MojitoPresentation.Base.Navigation.types;
const {selectEventItem} = MojitoServices.SportsContent.Events.selectors;
const {selectEventGroup, selectEventGroups} = MojitoServices.EventGroups.selectors;
const SportGroupsListTypes = MojitoModules.SportGroupsList.types;
const {TYPE_TABS} = MojitoModules.SportTabSelector.types;

const SLUGS = {
    EVENT_SLUG: 'eventSlug',
    TYPE_SLUG: 'typeSlug',
};

function getTypeSlug(sportId, typeId, groupId) {
    const groups = selectEventGroup(`${sportId}${groupId}`, getState());
    const type = groups?.find(group => group?.typeId === typeId);

    if (type) {
        return toSlug(type?.canonicalTypeName || type?.typeName || type?.name, Localization.getCurrentLanguageCode());
    }

    return '';
}

function getEventNameFromGroups(eventId) {
    const eventGroups = selectEventGroups(getState());

    for (const groups of Object.values(eventGroups || {})) {
        for (const group of groups || []) {
            const event = group?.events?.find(({id}) => String(id) === String(eventId));
            const eventName = event?.name || event?.canonicalName;

            if (eventName) {
                return eventName;
            }
        }
    }

    return '';
}

function getEventSlug(eventId) {
    const event = selectEventItem(eventId, getState());
    const name = event?.name || event?.canonicalName || getEventNameFromGroups(eventId);

    return name ? toSlug(name, Localization.getCurrentLanguageCode()) : '';
}

export default function initSlugs() {
    NavigationPathBuilder.toSportCompetitionsLeague = (sportId, typeId) => ({
        type: NAVIGATION.COMPETITIONS_LEAGUE,
        params: {
            [PARAMETER.SPORT_ID]: sportId,
            [PARAMETER.TYPE_ID]: typeId,
            [SLUGS.TYPE_SLUG]: getTypeSlug(sportId, typeId, SportGroupsListTypes.GROUP_ID.TOP_LEAGUES),
        },
    });

    NavigationPathBuilder.toSportMatchesLeague = (sportId, typeId) => ({
        ...NavigationPathBuilder.toSportCompetitionsLeague(sportId, typeId),
        nested: {
            type: NAVIGATION.MATCHES_LEAGUE,
        },
    });

    NavigationPathBuilder.toSportPriceBoostsLeague = (sportId, typeId) => ({
        ...NavigationPathBuilder.toSportCompetitionsLeague(sportId, typeId),
        nested: {
            type: NAVIGATION.PRICE_BOOSTS_LEAGUE,
        },
    });

    NavigationPathBuilder.toSportPreBetBuildersLeague = (sportId, typeId) => ({
        ...NavigationPathBuilder.toSportCompetitionsLeague(sportId, typeId),
        nested: {
            type: NAVIGATION.BET_BUILDERS_LEAGUE,
        },
    });

    NavigationPathBuilder.toSportTableLeague = (sportId, typeId) => ({
        ...NavigationPathBuilder.toSportCompetitionsLeague(sportId, typeId),
        nested: {
            type: NAVIGATION.TABLE_LEAGUE,
        },
    });

    NavigationPathBuilder.toSportOutrightsLeague = (sportId, typeId) => ({
        type: NAVIGATION.OUTRIGHTS_LEAGUE,
        params: {
            [PARAMETER.SPORT_ID]: sportId,
            [PARAMETER.TYPE_ID]: typeId,
            [SLUGS.TYPE_SLUG]: getTypeSlug(sportId, typeId, SportGroupsListTypes.GROUP_ID.TOP_OUTRIGHTS),
        },
    });

    NavigationPathBuilder.sportEventPath = (sportId, eventId, routeResolver) =>
        routeResolver.getRoute(NavigationPathBuilder.toSportEvent(sportId, eventId));

    NavigationPathBuilder.toSportEvent = (sportId, eventId) => ({
        type: NAVIGATION.SPORT_EVENT_PAGE,
        params: {
            [PARAMETER.SPORT_ID]: sportId || '',
            [PARAMETER.EVENT_ID]: eventId || '',
            [SLUGS.EVENT_SLUG]: getEventSlug(eventId),
        },
    });

    NavigationPathBuilder.inplayEventPath = (eventId, routeResolver) =>
        routeResolver.getRoute(NavigationPathBuilder.toInplayEvent(eventId));

    NavigationPathBuilder.toInplayEvent = eventId => ({
        type: NAVIGATION.INPLAY_EVENT_PAGE,
        params: {
            [PARAMETER.EVENT_ID]: eventId || '',
            [SLUGS.EVENT_SLUG]: getEventSlug(eventId),
        },
    });

    const typeTabRouteFnByName = {
        matches: NavigationPathBuilder.toSportMatchesLeague,
        priceBoosts: NavigationPathBuilder.toSportPriceBoostsLeague,
        preBuiltBetBuilders: NavigationPathBuilder.toSportPreBetBuildersLeague,
        table: NavigationPathBuilder.toSportTableLeague,
    };

    TYPE_TABS.forEach(tab => {
        const routeFn = typeTabRouteFnByName[tab.name];

        if (routeFn) {
            tab.routeFn = routeFn;
        }
    });
}
